import { normalizeKnowledgeFacts } from '../knowledgeItems/knowledgeFactNormalizer';
import { resolveSupportProfileFacts } from '../knowledgeItems/supportProfileResolver';
import { CARE_LENS, getCareLensKey } from '../../utils/careLens';
import { normalizeChapterProvenance } from './chapterProvenance';
import { resolveKnowledgeSourceAction } from './knowledgeSourceAction';
import { buildProfileSourcePresentation } from './profileSourcePresentation';
import {
  PROFILE_PLACEMENT_CHAPTERS,
  isExplicitProfilePlacement,
  isExplicitlyPlacedInAnotherChapter,
} from './profilePlacementMetadata';
import { resolveKnowledgeFactsForProfileChapter } from './profileKnowledgeProjection';
import { PROFILE_CHAPTER_TITLES } from '../../constants/profileChapterTitles';

export const WATCH_FOR_DOMAIN_ID = 'DM_04';

const GROUPS = Object.freeze({
  immediate_attention: 'Immediate attention',
  early_warning_signs: 'Early warning signs',
  common_triggers: 'Common triggers',
  when_to_act: 'When to act',
  normal_behavior: 'What may be normal',
});

const DEFAULT_GROUP_ORDER = Object.freeze([
  'immediate_attention',
  'early_warning_signs',
  'common_triggers',
  'when_to_act',
  'normal_behavior',
]);

const GROUP_PRIORITY = Object.freeze({
  immediate_attention: 500,
  when_to_act: 400,
  early_warning_signs: 300,
  common_triggers: 200,
  normal_behavior: 100,
});

const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const valueKey = (value = '') => cleanText(value).toLowerCase();
const getFactValue = (fact = {}) => cleanText(fact.summary || fact.title);

const buildProvenance = (facts = []) => {
  const sourceRefs = facts.flatMap((fact) => fact.sourceRefs || []);
  return normalizeChapterProvenance(sourceRefs, 'watch-for-source');
};

const classifyFact = (fact = {}) => {
  const text = getFactValue(fact).toLowerCase();
  if (/\b(?:requires?|needs?) immediate attention\b|\bseek immediate help\b/.test(text)) {
    return 'immediate_attention';
  }
  if (/\b(?:contact|call|notify) (?:the )?(?:caregiver|care team|nurse|clinician|doctor)\b/.test(text)) {
    return 'when_to_act';
  }
  if (/\b(?:is|may be) normal\b|\bdoes not (?:usually )?require intervention\b|\busual for (?:them|this person)\b/.test(text)) {
    return 'normal_behavior';
  }
  if (fact.profileField === 'thingsToAvoid'
    || fact.profileField === 'behavioralTriggers'
    || /\b(?:trigger|may lead to distress|causes? distress)\b/.test(text)) {
    return 'common_triggers';
  }
  if (fact.profileField === 'earlyWarningSigns'
    || /\b(?:early sign|warning sign|before becoming|faster than usual|stops? responding)\b/.test(text)) {
    return 'early_warning_signs';
  }
  if (fact.profileField === 'safetyConsiderations'
    && /\b(?:watch|risk|sign|change|notice)\b/.test(text)) {
    return 'early_warning_signs';
  }
  return '';
};

const buildHandbookCopy = (value = '') => {
  const text = cleanText(value).replace(/[.]+$/, '');
  const earlyMatch = text.match(/^(.+?) can be an? early sign of (.+)$/i);
  if (earlyMatch) {
    return { headline: earlyMatch[1], explanation: `Can be an early sign of ${earlyMatch[2]}.` };
  }
  const triggerMatch = text.match(/^(.+?) may trigger (.+)$/i);
  if (triggerMatch) {
    return { headline: triggerMatch[1], explanation: `May trigger ${triggerMatch[2]}.` };
  }
  const normalMatch = text.match(/^(.+?) is normal(?: and)? (.*)$/i);
  if (normalMatch) {
    return {
      headline: normalMatch[1],
      explanation: normalMatch[2] ? `This is normal and ${normalMatch[2]}.` : 'This may be normal for this person.',
    };
  }
  const immediateMatch = text.match(/^(.+?) requires? immediate attention$/i);
  if (immediateMatch) {
    return { headline: immediateMatch[1], explanation: 'This requires immediate attention.' };
  }
  const contactMatch = text.match(/^contact (?:the )?caregiver if (.+)$/i);
  if (contactMatch) {
    return { headline: contactMatch[1], explanation: `${text}.` };
  }
  return { headline: text, explanation: `${text}.` };
};

const deduplicateFacts = (facts = []) => {
  const output = [];
  const indexByValue = new Map();
  facts.forEach((fact) => {
    const key = valueKey(getFactValue(fact));
    if (!key) return;
    const existingIndex = indexByValue.get(key);
    if (existingIndex === undefined) {
      indexByValue.set(key, output.length);
      output.push(fact);
    } else if (output[existingIndex].synthetic && !fact.synthetic) {
      output[existingIndex] = fact;
    }
  });
  return output;
};

const getGroupOrder = (lens) => {
  if (lens === CARE_LENS.HOSPICE) {
    return ['immediate_attention', 'when_to_act', 'early_warning_signs', 'normal_behavior', 'common_triggers'];
  }
  if (lens === CARE_LENS.AUTISM) {
    return ['immediate_attention', 'early_warning_signs', 'common_triggers', 'normal_behavior', 'when_to_act'];
  }
  if (lens === CARE_LENS.DEMENTIA) {
    return ['immediate_attention', 'early_warning_signs', 'when_to_act', 'common_triggers', 'normal_behavior'];
  }
  return DEFAULT_GROUP_ORDER;
};

// DM_04: attach sourceAction only for real Knowledge facts, and only after
// classifyFact has already run (see buildWatchForDomain below) - the group a
// fact lands in (Immediate attention / trigger / normal / etc.) is decided
// purely from its text on every rebuild, so editing the source and
// regenerating the domain naturally reclassifies or drops the entry; nothing
// here needs to "undo" a prior classification. Synthetic legacy facts (from
// safetyConsiderations/behavioralTriggers, no real Knowledge doc behind them)
// never get a sourceAction.
const resolveFactSourceAction = (fact) => {
  if (fact.synthetic) return undefined;
  return resolveKnowledgeSourceAction(fact);
};

const buildLegacyTriggerFacts = (behavioralTriggers = []) => (
  (Array.isArray(behavioralTriggers) ? behavioralTriggers : [])
    .map(cleanText)
    .filter(Boolean)
    .map((value, index) => ({
      id: `legacy-medicalProfile:behavioralTriggers:${index}`,
      title: value,
      summary: value,
      profileField: 'behavioralTriggers',
      profileVisibilityOverride: null,
      responseGuidance: null,
      sourceRefs: [],
      synthetic: true,
    }))
);

export const buildWatchForDomain = ({
  child = {},
  knowledgeFacts = [],
  careContext = child.careContext || {},
  ageGroup = careContext?.ageGroup || '',
} = {}) => {
  const normalizedFacts = normalizeKnowledgeFacts(knowledgeFacts);
  const resolvedProfile = resolveSupportProfileFacts({
    knowledgeFacts: normalizedFacts,
    legacySupportProfile: child.supportProfile || child.supportGoals || {},
  });
  const fieldResolvedFacts = [
    ...(resolvedProfile.factsByField.earlyWarningSigns || []),
    ...(resolvedProfile.factsByField.thingsToAvoid || []),
    ...(resolvedProfile.factsByField.safetyConsiderations || []),
  ].filter((fact) => !isExplicitlyPlacedInAnotherChapter(fact, PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR));
  const fieldResolvedIds = new Set(fieldResolvedFacts.map((fact) => fact.id));
  const candidateFacts = [
    ...fieldResolvedFacts,
    ...resolveKnowledgeFactsForProfileChapter(
      normalizedFacts,
      PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR
    ).filter((fact) => !fieldResolvedIds.has(fact.id)),
    ...buildLegacyTriggerFacts(child.medicalProfile?.behavioralTriggers),
  ];
  const classifiedFacts = deduplicateFacts(candidateFacts).map((fact, index) => ({
    fact,
    groupId: classifyFact(fact) || (
      fact.profileChapterId === PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR
        ? 'early_warning_signs'
        : ''
    ),
    index,
  })).filter((item) => item.groupId);
  const rankedFacts = classifiedFacts.sort((left, right) => (
    Number(Boolean(right.fact.profileVisibilityOverride === 'pin'))
      - Number(Boolean(left.fact.profileVisibilityOverride === 'pin'))
    || GROUP_PRIORITY[right.groupId] - GROUP_PRIORITY[left.groupId]
    || left.index - right.index
  ));
  const lens = getCareLensKey({
    selectedConditions: child.conditions || [],
    careContext: careContext || child.careContext || {},
  });
  const groupOrder = getGroupOrder(lens);
  const handbookGroups = groupOrder.map((groupId) => ({
    id: groupId,
    title: GROUPS[groupId],
    entries: rankedFacts.filter((item) => item.groupId === groupId).map(({ fact }) => ({
      id: `entry-${fact.id}`,
      knowledgeFactId: fact.synthetic ? undefined : fact.id,
      ...buildHandbookCopy(getFactValue(fact)),
      storyText: getFactValue(fact),
      responseGuidance: fact.responseGuidance || undefined,
      provenance: fact.synthetic ? undefined : buildProvenance([fact]),
      sourcePresentation: fact.synthetic ? undefined : buildProfileSourcePresentation(fact),
      sourceAction: resolveFactSourceAction(fact),
    })),
  })).filter((group) => group.entries.length);
  const realFacts = rankedFacts.map((item) => item.fact).filter((fact) => !fact.synthetic);

  return {
    id: 'watch-for',
    title: PROFILE_CHAPTER_TITLES.WATCH_FOR,
    description: 'Early signs, changes, or triggers to pay attention to.',
    caregiverQuestion: 'What should I watch for?',
    briefing: {
      items: rankedFacts.slice(0, 3).map(({ fact }) => ({
        id: `briefing-${fact.id}`,
        text: getFactValue(fact).replace(/[.]+$/, ''),
        explainedByEntryIds: [`entry-${fact.id}`],
      })),
      provenance: buildProvenance(realFacts),
    },
    handbook: { groups: handbookGroups },
    emptyState: {
      title: 'What should I watch for?',
      description: 'Add changes, triggers, warning signs, or situations that caregivers should notice.',
    },
    metadata: {
      domainId: WATCH_FOR_DOMAIN_ID,
      careFocus: lens,
      ageGroup: ageGroup || careContext?.ageGroup || 'unknown',
      contractVersion: '1',
    },
  };
};
